/**
 * EstimateDraftPanel - Phase 3/4 Estimate Builder UI with Review Workflow
 * 
 * Features:
 * - Phase 4: Review queue with pending/approved/rejected sections
 * - Display estimate items with checkboxes
 * - Show labor minutes per item
 * - Approve/Reject actions for managers/writers
 * - Apply only approved items to ticket
 * - Parts placeholders labeled as TBD
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Loader2, 
  Plus, 
  ChevronDown, 
  ChevronUp,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Wrench,
  ThumbsUp,
  ThumbsDown,
  RotateCcw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EstimateItem } from '@/hooks/useDviEstimate';
import { OverrideReasonContent } from './OverrideReasonModal';

interface EstimateDraftPanelProps {
  items: EstimateItem[];
  totalLaborMinutes: number;
  onApplySelected: (itemIds: string[]) => void;
  onApplyAll: () => void;
  isApplying: boolean;
  // Phase 4: Review actions
  onApproveItems?: (itemIds: string[]) => void;
  onRejectItems?: (itemIds: string[], reason?: string) => void;
  isReviewing?: boolean;
  onJobsAdded?: () => void;
}

const CONDITION_CONFIG = {
  green: { label: 'Good', icon: CheckCircle, className: 'bg-green-500/10 text-green-600' },
  yellow: { label: 'Monitor', icon: AlertTriangle, className: 'bg-yellow-500/10 text-yellow-600' },
  red: { label: 'Urgent', icon: XCircle, className: 'bg-red-500/10 text-red-600' },
};

const REVIEW_STATUS_CONFIG = {
  pending_review: { label: 'Pending', className: 'bg-yellow-500/10 text-yellow-600 border-yellow-200' },
  approved: { label: 'Approved', className: 'bg-green-500/10 text-green-600 border-green-200' },
  rejected: { label: 'Rejected', className: 'bg-red-500/10 text-red-600 border-red-200' },
};

export function EstimateDraftPanel({
  items,
  totalLaborMinutes,
  onApplySelected,
  onApplyAll,
  isApplying,
  onApproveItems,
  onRejectItems,
  isReviewing,
  onJobsAdded,
}: EstimateDraftPanelProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [itemsToReject, setItemsToReject] = useState<string[]>([]);

  // Phase 4: Group items by status
  const pendingReviewItems = items.filter(item => 
    item.review_status === 'pending_review' && !item.added_to_ticket_at
  );
  const approvedItems = items.filter(item => 
    item.review_status === 'approved' && !item.added_to_ticket_at
  );
  const rejectedItems = items.filter(item => 
    item.review_status === 'rejected' && !item.added_to_ticket_at
  );
  const appliedItems = items.filter(item => !!item.added_to_ticket_at);

  const toggleSelected = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAllPending = () => {
    setSelectedIds(new Set(pendingReviewItems.map(i => i.id)));
  };

  const selectAllApproved = () => {
    setSelectedIds(new Set(approvedItems.map(i => i.id)));
  };

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const selectedPendingMinutes = pendingReviewItems
    .filter(i => selectedIds.has(i.id))
    .reduce((sum, i) => sum + i.labor_minutes, 0);

  const selectedApprovedMinutes = approvedItems
    .filter(i => selectedIds.has(i.id))
    .reduce((sum, i) => sum + i.labor_minutes, 0);

  const handleApproveSelected = async () => {
    const selectedPendingIds = pendingReviewItems
      .filter(i => selectedIds.has(i.id))
      .map(i => i.id);
    if (selectedPendingIds.length > 0 && onApproveItems) {
      await onApproveItems(selectedPendingIds);
      setSelectedIds(new Set());
    }
  };

  const handleRejectSelected = () => {
    const selectedPendingIds = pendingReviewItems
      .filter(i => selectedIds.has(i.id))
      .map(i => i.id);
    if (selectedPendingIds.length > 0) {
      setItemsToReject(selectedPendingIds);
      setRejectDialogOpen(true);
    }
  };

  const confirmReject = async () => {
    if (itemsToReject.length > 0 && onRejectItems) {
      await onRejectItems(itemsToReject, rejectionReason || undefined);
      setSelectedIds(new Set());
      setRejectDialogOpen(false);
      setRejectionReason('');
      setItemsToReject([]);
    }
  };

  const handleApplySelected = async () => {
    const selectedApprovedIds = approvedItems
      .filter(i => selectedIds.has(i.id))
      .map(i => i.id);
    if (selectedApprovedIds.length > 0) {
      await onApplySelected(selectedApprovedIds);
      setSelectedIds(new Set());
      onJobsAdded?.();
    }
  };

  const handleApplyAll = async () => {
    await onApplyAll();
    setSelectedIds(new Set());
    onJobsAdded?.();
  };

  const handleRestoreItem = async (itemId: string) => {
    // Restore rejected item to pending_review
    if (onApproveItems) {
      // Use approve to move from rejected → approved (then can be re-reviewed)
      await onApproveItems([itemId]);
    }
  };

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          <Wrench className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No estimate items yet</p>
          <p className="text-xs mt-1">Build an estimate from analyzed findings</p>
        </CardContent>
      </Card>
    );
  }

  const renderItemCard = (item: EstimateItem, section: 'pending' | 'approved' | 'rejected' | 'applied') => {
    const condition = item.condition;
    const config = condition ? CONDITION_CONFIG[condition] : null;
    const reviewConfig = REVIEW_STATUS_CONFIG[item.review_status];
    const isSelected = selectedIds.has(item.id);
    const isExpanded = expandedIds.has(item.id);
    const isSelectable = section === 'pending' || section === 'approved';

    return (
      <Card 
        key={item.id} 
        className={cn(
          'transition-colors',
          isSelected && 'ring-2 ring-primary/50',
          condition === 'red' && 'border-red-200',
          condition === 'yellow' && 'border-yellow-200',
          section === 'applied' && 'opacity-60',
        )}
      >
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start gap-3">
            {/* Checkbox (only for pending/approved) */}
            {isSelectable && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleSelected(item.id)}
                className="mt-0.5"
              />
            )}

            <div className="flex-1 min-w-0">
              {/* Header Row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  {config && (
                    <Badge variant="outline" className={cn('shrink-0 text-xs gap-1', config.className)}>
                      <config.icon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  )}
                  {/* Phase 4: Review status badge */}
                  <Badge variant="outline" className={cn('shrink-0 text-xs', reviewConfig.className)}>
                    {reviewConfig.label}
                  </Badge>
                  <span className="font-medium text-sm truncate">
                    {item.title || 'Estimate Item'}
                  </span>
                </div>
                <Badge variant="secondary" className="shrink-0 text-xs">
                  {item.labor_minutes} min
                </Badge>
              </div>

              {/* Rejection reason */}
              {item.rejection_reason && (
                <p className="text-xs text-red-600 mt-1">
                  Reason: {item.rejection_reason}
                </p>
              )}

              {/* Restore button for rejected items */}
              {section === 'rejected' && onApproveItems && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs gap-1 mt-1"
                  onClick={() => handleRestoreItem(item.id)}
                  disabled={isReviewing}
                >
                  <RotateCcw className="h-3 w-3" />
                  Restore
                </Button>
              )}

              {/* Expandable Details */}
              <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(item.id)}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-5 px-0 text-xs gap-1 text-muted-foreground">
                    {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                    {isExpanded ? 'Less' : 'More'}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2 space-y-2">
                  {/* Procedure */}
                  {item.procedure_outline && (
                    <div className="bg-muted/50 rounded p-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Procedure:</p>
                      <p className="text-xs whitespace-pre-line">{item.procedure_outline}</p>
                    </div>
                  )}

                  {/* Parts Placeholders */}
                  {item.parts_placeholders?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Parts (TBD - pricing pending):
                      </p>
                      <ul className="text-xs space-y-0.5">
                        {item.parts_placeholders.map((part, i) => (
                          <li key={i}>• {part.name} × {part.quantity}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Shop Supplies */}
                  {item.shop_supplies?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Shop Supplies:</p>
                      <p className="text-xs">{item.shop_supplies.join(', ')}</p>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header with totals */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {totalLaborMinutes} min total
          </Badge>
        </div>
        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectNone}>
          Clear Selection
        </Button>
      </div>

      {/* Phase 4: Pending Review Section */}
      {pendingReviewItems.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Pending Review ({pendingReviewItems.length})
            </h4>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllPending}>
              Select All
            </Button>
          </div>
          <div className="space-y-2">
            {pendingReviewItems.map(item => renderItemCard(item, 'pending'))}
          </div>
          {/* Review Actions */}
          {onApproveItems && onRejectItems && (
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1 text-green-600 border-green-200 hover:bg-green-50"
                onClick={handleApproveSelected}
                disabled={!pendingReviewItems.some(i => selectedIds.has(i.id)) || isReviewing}
              >
                {isReviewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
                Approve Selected
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1 text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleRejectSelected}
                disabled={!pendingReviewItems.some(i => selectedIds.has(i.id)) || isReviewing}
              >
                {isReviewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsDown className="h-3 w-3" />}
                Reject Selected
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Phase 4: Approved Section (ready to apply) */}
      {approvedItems.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Approved ({approvedItems.length})
            </h4>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllApproved}>
              Select All
            </Button>
          </div>
          <div className="space-y-2">
            {approvedItems.map(item => renderItemCard(item, 'approved'))}
          </div>
          {/* Apply Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 gap-1"
              onClick={handleApplySelected}
              disabled={!approvedItems.some(i => selectedIds.has(i.id)) || isApplying}
            >
              {isApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Apply Selected • {selectedApprovedMinutes} min
            </Button>
            <Button
              variant="default"
              size="sm"
              className="gap-1"
              onClick={handleApplyAll}
              disabled={isApplying}
            >
              {isApplying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Apply All
            </Button>
          </div>
        </div>
      )}

      {/* Phase 4: Rejected Section (collapsed) */}
      {rejectedItems.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground">
              <XCircle className="h-4 w-4 text-red-500" />
              Rejected ({rejectedItems.length})
              <ChevronDown className="h-3 w-3 ml-auto" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2">
            {rejectedItems.map(item => renderItemCard(item, 'rejected'))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Applied Items (collapsed) */}
      {appliedItems.length > 0 && (
        <Collapsible>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground">
              <CheckCircle className="h-4 w-4 text-green-600" />
              Already Applied ({appliedItems.length})
              <ChevronDown className="h-3 w-3 ml-auto" />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-2 pt-2">
            {appliedItems.map(item => renderItemCard(item, 'applied'))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Phase 5: Override Reason Dialog with structured categories */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Items</DialogTitle>
            <DialogDescription>
              Select a reason for rejecting {itemsToReject.length} item(s). This helps OptiDVI learn from advisor decisions.
            </DialogDescription>
          </DialogHeader>
          <OverrideReasonContent
            rejectionReason={rejectionReason}
            onReasonChange={setRejectionReason}
            onConfirm={confirmReject}
            onCancel={() => setRejectDialogOpen(false)}
            isSubmitting={isReviewing}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
